import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Brain, CheckCircle, XCircle, Coins, Trophy } from '@phosphor-icons/react';
import { Button } from './ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const DailyQuizModal = ({ quiz, alreadyDone, onClose }) => {
  const { fetchUser } = useAuth();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!quiz) return null;

  const total = quiz.questions.length;
  const allAnswered = Object.keys(answers).length === total;

  const submit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const ordered = quiz.questions.map((_, i) => answers[i] ?? -1);
      const res = await api.post('/learning/daily-quiz/submit', {
        quiz_id: quiz.id,
        answers: ordered,
      });
      setResult(res.data);
      if (res.data.reward_cash > 0) {
        toast.success(`🎉 Perfect! +$${res.data.reward_cash} cash added!`, { duration: 6000 });
      }
      await fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="daily-quiz-modal"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-2xl glass-card overflow-hidden relative my-8"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300 z-10"
            data-testid="quiz-close"
            aria-label="Close quiz"
          >
            <X size={18} />
          </button>

          <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 p-7 pb-12 relative">
            <div className="flex items-center gap-2 mb-1.5">
              <p className="text-white/70 text-xs font-bold tracking-widest uppercase">🧠 Daily Quiz</p>
              {quiz.difficulty && (
                <span
                  data-testid="quiz-difficulty-badge"
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    quiz.difficulty === 'easy'   ? 'bg-emerald-500/30 text-emerald-100' :
                    quiz.difficulty === 'medium' ? 'bg-yellow-500/30 text-yellow-100' :
                                                   'bg-red-500/30 text-red-100'
                  }`}
                >
                  {quiz.difficulty}
                </span>
              )}
            </div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-black/30 backdrop-blur mb-2">
              <Brain size={28} weight="fill" className="text-white" />
            </div>
            <h2 className="text-2xl font-unbounded font-bold text-white">{quiz.theme}</h2>
            <p className="text-white/80 text-sm mt-1">
              Get all {total} right → <span className="font-bold">+$50 portfolio cash</span>
            </p>
          </div>

          <div className="-mt-7 mx-5 mb-5 max-h-[60vh] overflow-y-auto">
            {alreadyDone && !result && (
              <div className="glass rounded-2xl p-5 mb-4 text-center">
                <Trophy size={36} weight="fill" className="text-gold mx-auto mb-2" />
                <p className="text-white font-bold mb-1">You already completed today's quiz!</p>
                <p className="text-zinc-400 text-sm">Come back tomorrow for a new one. 📅</p>
              </div>
            )}

            {!alreadyDone && !result && (
              <div className="glass rounded-2xl p-5 space-y-5">
                {quiz.questions.map((q, qi) => (
                  <div key={qi} data-testid={`quiz-question-${qi}`}>
                    <p className="text-white font-medium mb-3">
                      <span className="text-gold font-bold mr-2">Q{qi + 1}.</span>
                      {q.q}
                    </p>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => {
                        const selected = answers[qi] === oi;
                        return (
                          <button
                            key={oi}
                            onClick={() => setAnswers({ ...answers, [qi]: oi })}
                            className={`w-full text-left p-3 rounded-xl border transition-all text-sm ${
                              selected
                                ? 'bg-purple-500/20 border-purple-400 text-white'
                                : 'bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10 hover:border-white/20'
                            }`}
                            data-testid={`quiz-q${qi}-option-${oi}`}
                          >
                            <span className="inline-block w-5 h-5 rounded-full mr-2 text-xs font-bold leading-5 text-center bg-white/10">
                              {String.fromCharCode(65 + oi)}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {result && (
              <div className="glass rounded-2xl p-5">
                <div className="text-center mb-5 pb-5 border-b border-white/10">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                    result.perfect ? 'bg-gold/20' : 'bg-zinc-700/40'
                  }`}>
                    {result.perfect ? (
                      <Coins size={36} weight="fill" className="text-gold" />
                    ) : (
                      <Brain size={36} weight="fill" className="text-zinc-300" />
                    )}
                  </div>
                  <h3 className="text-2xl font-unbounded font-bold text-white mb-1">
                    {result.score}/{result.total} correct
                  </h3>
                  <p className={`text-sm font-medium ${result.perfect ? 'text-gold' : 'text-zinc-400'}`}>
                    {result.message}
                  </p>
                  {result.reward_cash > 0 && (
                    <p className="mt-2 text-xl font-mono font-bold text-gold" data-testid="quiz-reward">
                      +${result.reward_cash} cash 💰
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  {quiz.questions.map((q, qi) => {
                    const fb = result.feedback[qi];
                    return (
                      <div
                        key={qi}
                        className={`p-3 rounded-xl border ${
                          fb.is_correct ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {fb.is_correct
                            ? <CheckCircle size={18} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                            : <XCircle size={18} weight="fill" className="text-red-400 flex-shrink-0 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium">{q.q}</p>
                            {!fb.is_correct && (
                              <p className="text-emerald-300 text-xs mt-1">
                                Correct answer: <span className="font-bold">{q.options[fb.correct_index]}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="px-5 pb-5">
            {!result && !alreadyDone ? (
              <Button
                onClick={submit}
                disabled={!allAnswered || submitting}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-600 text-white font-bold rounded-full"
                data-testid="quiz-submit"
              >
                {submitting ? 'Scoring...' : allAnswered ? 'Submit & See Results' : `Answer all ${total} first`}
              </Button>
            ) : (
              <Button
                onClick={onClose}
                className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold rounded-full"
                data-testid="quiz-done"
              >
                Done
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DailyQuizModal;
